import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import type { RevisionEntry, RevisionType } from '@/api/types/Audit';
import { getJizdaHistory } from '@/api/services/jizdyService';
import { ModalContentSkeleton } from '@/components/loading';
import { StatusChip } from '@/components/shell';
import { useAppSelector } from '@/store/hooks';
import { selectAssignedUserName } from '@/store/usersSlice';

// CHR-02 / ADR-0036: jízda is `@Audited` directly. History is now flat scalar
// diffs — the per-revision loadings sub-table is gone (no child collection).
export type AuditEntityKind = 'jizda';

export interface AuditHistoryModalProps {
  entityKind: AuditEntityKind;
  entityId: string | null;
  onClose: () => void;
}

const REV_LIST_WIDTH = 320;
const TAP_TARGET_MIN_PX = 44;

// Snapshot fields the api may emit as `null` on historical reconstruction
// (Envers limitation — OQ). We skip them in the snapshot table to avoid
// rendering misleading "—" entries.
const SKIPPED_SNAPSHOT_KEYS = new Set([
  'id',
  'version',
  'createdAt',
  'updatedAt',
  // Raw FK ids — `customerName` carries the human-readable version.
  'customerId',
]);

const formatCsDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatSnapshotValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  return JSON.stringify(value);
};

// Snapshot field keys whose raw value is a KC user sub that should be
// resolved via the dispatchers cache. Today only `assignedDispatcherSub`
// applies; if future entities snapshot more user-typed fields, add them here.
const DISPATCHER_SUB_KEYS = new Set(['assignedDispatcherSub']);

// Resolves a snapshot value for rendering. If the field is a dispatcher-sub
// key and the raw value is a UUID string, returns the resolved display name
// (or `…last8` fallback when the sub isn't in the cache — covers historical
// snapshots of since-deactivated dispatchers). For every other key/value it
// delegates to `formatSnapshotValue`.
const useResolvedSnapshotValue = (key: string, value: unknown): string => {
  const isDispatcherSub =
    DISPATCHER_SUB_KEYS.has(key) && typeof value === 'string' && value.length > 0;
  // Selectors must be called unconditionally — pass null when not a sub so
  // the selector returns '' and we fall through to formatSnapshotValue.
  const resolved = useAppSelector(
    selectAssignedUserName(isDispatcherSub ? (value as string) : null),
  );
  if (isDispatcherSub) return resolved;
  return formatSnapshotValue(value);
};

interface SnapshotRowProps {
  fieldKey: string;
  value: unknown;
  isChanged: boolean;
  label: string;
}

const SnapshotRow = ({
  fieldKey,
  value,
  isChanged,
  label,
}: SnapshotRowProps): JSX.Element => {
  const theme = useTheme();
  const displayValue = useResolvedSnapshotValue(fieldKey, value);
  return (
    <Box sx={{ display: 'contents' }} data-testid={`audit-snap-${fieldKey}`}>
      <Typography
        component="dt"
        sx={{
          fontSize: 12,
          color: theme.palette.text.secondary,
          fontWeight: isChanged ? 600 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{
          m: 0,
          fontSize: 13,
          color: theme.palette.text.primary,
          fontWeight: isChanged ? 600 : 400,
          fontFamily: theme.typography.fontFamily,
          wordBreak: 'break-word',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

interface OpChipProps {
  type: RevisionType;
}

const OpChip = ({ type }: OpChipProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  // Op color matches the mockup `.rev-op` semantics (mockups.html ~L841):
  //   create → green, update → blue, delete → red. Driven by the canonical
  //   DESIGN.md §2 status tokens (`theme.statusPalette`) rather than MUI's
  //   auto-derived `success/info/error` light/dark (those are off-token —
  //   the palette only defines {main, contrastText}).
  const palette =
    type === 'ADD'
      ? theme.statusPalette.done // green (DCFCE7/15803D)
      : type === 'DEL'
        ? theme.statusPalette.paused // red (FEE2E2/B91C1C)
        : theme.statusPalette.planned; // blue (DBEAFE/1D4ED8)
  return (
    <Chip
      size="small"
      label={t(`history.type.${type}`)}
      sx={{
        bgcolor: palette.bg,
        color: palette.color,
        fontWeight: 600,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    />
  );
};

export const AuditHistoryModal = ({
  entityId,
  onClose,
}: AuditHistoryModalProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [revisions, setRevisions] = useState<RevisionEntry[]>([]);
  const [selectedRevisionNumber, setSelectedRevisionNumber] = useState<number | null>(
    null,
  );
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const open = entityId !== null;

  useEffect(() => {
    if (!open || !entityId) return;
    let cancelled = false;
    setStatus('loading');
    setRevisions([]);
    setSelectedRevisionNumber(null);
    getJizdaHistory(entityId)
      .then((data) => {
        if (cancelled) return;
        setRevisions(data);
        setSelectedRevisionNumber(data[0]?.revisionNumber ?? null);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [entityId, open]);

  const selected = useMemo(
    () => revisions.find((r) => r.revisionNumber === selectedRevisionNumber) ?? null,
    [revisions, selectedRevisionNumber],
  );

  const handleSelect = useCallback((n: number) => {
    setSelectedRevisionNumber(n);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isCompact}
      aria-labelledby="audit-history-title"
    >
      <DialogTitle
        id="audit-history-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: theme.spacing(1.5),
        }}
      >
        <Typography component="span" sx={{ fontWeight: 600, fontSize: 16, flex: 1 }}>
          {t('history.title')}
        </Typography>
        {status === 'ready' ? (
          <Chip
            size="small"
            label={`${revisions.length}`}
            sx={{
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.secondary,
              fontFamily: theme.monospaceFontFamily,
              fontSize: 11,
            }}
          />
        ) : null}
        <IconButton
          onClick={onClose}
          aria-label={t('history.close')}
          sx={{
            width: TAP_TARGET_MIN_PX,
            height: TAP_TARGET_MIN_PX,
            color: theme.palette.text.secondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {status === 'loading' ? (
          <Box sx={{ p: theme.spacing(3) }} data-testid="audit-history-loading">
            <ModalContentSkeleton variant="history" ariaLabel={t('history.loading')} />
          </Box>
        ) : status === 'error' ? (
          <Box sx={{ p: theme.spacing(3) }}>
            <Alert severity="error" data-testid="audit-history-error">
              {t('history.error')}
            </Alert>
          </Box>
        ) : revisions.length === 0 ? (
          <Box sx={{ p: theme.spacing(3) }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {t('history.empty')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isCompact ? 'column' : 'row',
              minHeight: isCompact ? 'auto' : 480,
            }}
            data-testid="audit-history-split"
          >
            {/* Left pane — revision list */}
            <Box
              sx={{
                width: isCompact ? '100%' : REV_LIST_WIDTH,
                flexShrink: 0,
                borderRight: isCompact
                  ? 'none'
                  : `1px solid ${theme.palette.divider}`,
                borderBottom: isCompact
                  ? `1px solid ${theme.palette.divider}`
                  : 'none',
                bgcolor: theme.palette.background.default,
                overflowY: 'auto',
                maxHeight: isCompact ? 240 : 'auto',
              }}
              data-testid="audit-revlist"
            >
              {revisions.map((rev) => {
                const isActive = rev.revisionNumber === selectedRevisionNumber;
                return (
                  <Box
                    key={rev.revisionNumber}
                    onClick={() => handleSelect(rev.revisionNumber)}
                    data-testid={`audit-revitem-${rev.revisionNumber}`}
                    data-active={isActive ? 'true' : 'false'}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(rev.revisionNumber);
                      }
                    }}
                    sx={{
                      p: theme.spacing(1.5, 2),
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderLeft: `3px solid ${
                        isActive ? theme.palette.primary.main : 'transparent'
                      }`,
                      bgcolor: isActive
                        ? theme.palette.primary.light
                        : 'transparent',
                      minHeight: TAP_TARGET_MIN_PX,
                      '&:hover': {
                        bgcolor: isActive
                          ? theme.palette.primary.light
                          : theme.palette.background.paper,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <OpChip type={rev.revisionType} />
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: theme.monospaceFontFamily,
                          fontSize: 11,
                          color: theme.palette.text.secondary,
                          ml: 'auto',
                        }}
                      >
                        #{rev.seq}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 13,
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {rev.userDisplayName || t('history.unknownUser')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: theme.monospaceFontFamily,
                        fontSize: 11,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatCsDateTime(rev.revisionTimestamp)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Right pane — selected revision detail */}
            <Box
              sx={{
                flex: 1,
                p: theme.spacing(3),
                overflowY: 'auto',
                bgcolor: theme.palette.background.paper,
              }}
              data-testid="audit-snapshot"
            >
              {selected ? (
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <Typography
                      component="h4"
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('history.revisionLabel', { n: selected.seq })}
                    </Typography>
                    <OpChip type={selected.revisionType} />
                    <StatusChip
                      status="planned"
                      label={t('history.selected')}
                      variant="plain"
                      data-testid="audit-selected-chip"
                    />
                    <Typography
                      sx={{
                        fontFamily: theme.monospaceFontFamily,
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatCsDateTime(selected.revisionTimestamp)}
                    </Typography>
                  </Stack>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {t('history.byUser')}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: theme.palette.text.primary }}>
                      {selected.userDisplayName || t('history.unknownUser')}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {t('history.changedFields')}
                    </Typography>
                    {selected.changedFields.length === 0 ? (
                      <Typography
                        sx={{ fontSize: 13, color: theme.palette.text.disabled }}
                        data-testid="audit-no-changes"
                      >
                        {t('history.noChanges')}
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={0.75} sx={{ flexWrap: 'wrap' }}>
                        {selected.changedFields.map((field) => (
                          <Chip
                            key={field}
                            size="small"
                            label={t(`history.field.${field}`, { defaultValue: field })}
                            sx={{
                              bgcolor: theme.palette.primary.light,
                              color: theme.palette.primary.dark,
                              fontSize: 11,
                              fontWeight: 500,
                            }}
                            data-testid={`audit-changed-${field}`}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {t('history.snapshot')}
                    </Typography>
                    <Box
                      component="dl"
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(160px, max-content) 1fr',
                        rowGap: theme.spacing(0.5),
                        columnGap: theme.spacing(2),
                        m: 0,
                      }}
                    >
                      {Object.entries(selected.snapshot)
                        .filter(([key, val]) => {
                          if (SKIPPED_SNAPSHOT_KEYS.has(key)) return false;
                          // Drop null fields that the api emits as placeholders
                          // on historical reconstruction (Envers limitation).
                          return val !== null && val !== undefined;
                        })
                        .map(([key, val]) => (
                          <SnapshotRow
                            key={key}
                            fieldKey={key}
                            value={val}
                            isChanged={selected.changedFields.includes(key)}
                            label={t(`history.field.${key}`, { defaultValue: key })}
                          />
                        ))}
                    </Box>
                  </Box>
                </Stack>
              ) : null}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
